// @flow
import * as React from "react"
import styled from "styled-components"
import Alert from "@kiwicom/orbit-components/lib/Alert"

import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"

type Props = {
  phraseAppId: string,
  shouldRequirePaymentSpecialInfo: boolean,
  handleCardTypeChange: string => void,
}

type State = {}

export const getIframeDomain = (bookingPayment: string) => {
  if (bookingPayment === "iframe") return "https://fe.payments-kiwi.com"
  if (bookingPayment === "iframe-dev") return "https://fe.payments-kiwi-dev.com"
  throw new Error(`Invalid BOOKING_PAYMENT test value ${bookingPayment}`)
}

const getIframeOrigin = () => {
  const bookingPayment =
    typeof window !== "undefined" &&
    window.reduxStore &&
    window.reduxStore.getState().splitster.get("BOOKING_PAYMENT").value
  return getIframeDomain(String(bookingPayment))
}

if (typeof window !== "undefined") {
  window.missingIframe = function missingIframe() {
    cuckoo.track(cuckooEvents.PCI_IFRAME_ALTERNATE_TEXT_DISPLAYED)
  }
}

export function postMessageToPayment(msg: string) {
  try {
    const paymentIframe = document.getElementById("paymentIframe")
    if (paymentIframe && paymentIframe instanceof HTMLIFrameElement) {
      // Without * it does not work on localhost
      const postTarget = window.location.hostname === "localhost" ? "*" : getIframeOrigin()
      paymentIframe.contentWindow.postMessage(msg, postTarget)
    } else {
      console.error("Payment iframe not available")
      cuckoo.track(cuckooEvents.PCI_IFRAME_POST_MESSAGE_FAILED, {
        error: "Payment iframe not available",
      })
    }
  } catch (err) {
    cuckoo.track(cuckooEvents.PCI_IFRAME_POST_MESSAGE_FAILED, { error: err.message })
  }
}

export function validateIframePayment() {
  postMessageToPayment("checkValidity")
}

export const tryGetValue = (data: string) => (prefix: string) =>
  data.startsWith(prefix) ? data.substr(prefix.length) : null

type Data = {
  [string]: any,
}

function fillSpecialInfoBeforeSubmit(paymentSpecialInfo: Data = {}) {
  // remove unused props
  const { email, formattedExpirationDate, formattedPhoneNumber, ...data } = paymentSpecialInfo
  Object.keys(data).forEach(key => {
    if (data[key]) postMessageToPayment(`autofill.paymentSpecialInfo.${key}:${data[key]}`)
  })
}

type Response = {
  token: string,
  encrypted_cvv?: string,
}

export function iframeProcessPayment(
  data: Data,
  paymentSpecialInfo?: { [string]: any } | null = {},
): Promise<Response> {
  if (paymentSpecialInfo) fillSpecialInfoBeforeSubmit(paymentSpecialInfo)

  Object.keys(data).forEach(key => {
    if (data[key]) postMessageToPayment(`extraData.${key}:${data[key]}`)
  })

  let messageResolver = null
  const processPayment = new Promise((resolve, reject) => {
    messageResolver = e => {
      if (e.origin !== getIframeOrigin()) return

      if (e.data.startsWith("result:")) {
        // Not tracking result
        resolve(JSON.parse(e.data.substr("result:".length)))
        return
      }

      if (e.data.startsWith("error:")) {
        // Maybe add JSON.parse ...
        reject(e.data.substr("error:".length))
      }
    }

    window.addEventListener("message", messageResolver, false)
    postMessageToPayment(`processPayment`)

    // I'm not sure about timeout, meybe we don't need it at all
    setTimeout(() => {
      reject(new Error("timeout"))
    }, 1000 * 60 * 19) // 19 min timeout
  })
  return processPayment
    .then(result => {
      window.removeEventListener("message", messageResolver)
      return result
    })
    .catch(ex => {
      console.error("exception while processing Payment", ex)
      window.removeEventListener("message", messageResolver)
      throw ex
    })
}

// min height set to 82 = wide extended version
const IframeWrapper = styled.div`
  position: relative;
  min-height: 82px;
  width: 100%;
  iframe {
    overflow: hidden;
    z-index: 2;
    position: relative;
    background: white;
  }
  > div {
    z-index: 1;
    position: absolute;
    top: 0;
  }
`

export default class PaymentIframe extends React.Component<Props, State> {
  loadedTimeout: ?TimeoutID = null

  firstMessageReceived = false

  componentDidMount() {
    this.loadedTimeout = setTimeout(this.handleTimeout, 10000)
    window.addEventListener("message", this.listener, false)
  }

  componentDidUpdate(prevProps: Props) {
    const { shouldRequirePaymentSpecialInfo } = this.props
    if (shouldRequirePaymentSpecialInfo !== prevProps.shouldRequirePaymentSpecialInfo) {
      postMessageToPayment(`switchVersion:${String(shouldRequirePaymentSpecialInfo)}`)
    }
  }

  componentWillUnmount() {
    if (this.loadedTimeout !== null) clearTimeout(this.loadedTimeout)
    if (this.listener) window.removeEventListener("message", this.listener)
  }

  handleTimeout = () => {
    // iframe failed to load after 10 seconds
    cuckoo.track(cuckooEvents.PCI_IFRAME_TIMEOUT)
  }

  listener = (e: MessageEvent) => {
    // Note that webpack also sending some messages...
    if (e.origin !== getIframeOrigin()) return

    if (!this.firstMessageReceived) {
      this.firstMessageReceived = true
      cuckoo.track(cuckooEvents.PCI_IFRAME_FIRST_MESSAGE_RECEIVED)
    }

    const data = String(e.data)
    const getValue = tryGetValue(data)

    if (data === "webPaymentPayAvailable") {
      cuckoo.track(cuckooEvents.PCI_IFRAME_WEB_PAYMENT_AVAILABLE)
      return
    }

    const iframeHeight = getValue("iframeHeight:")
    if (iframeHeight) {
      cuckoo.track(cuckooEvents.PCI_IFRAME_SETTING_HEIGHT, { height: iframeHeight })
      const iframe = document.getElementById("paymentIframe")
      if (iframe && Number(iframeHeight) > 0) {
        iframe.setAttribute("height", iframeHeight)
      } else {
        cuckoo.track(cuckooEvents.PCI_IFRAME_CANNOT_SET_HEIGHT)
      }
      return
    }

    // IMPORTANT: do not process payment results in this listener,
    // otherwise tokenized card will be stored in cuckoo
    if (data.startsWith("result:")) return

    cuckoo.track(cuckooEvents.PCI_IFRAME_MESSAGE_RECEIVED, { data })

    if (data.startsWith("checkValidity:") || data.startsWith("recognizedCardType:")) {
      const [id, value] = data.split(":")
      // can be changed to use state or redux
      const el = document.getElementById(`paymentIframe_${id}`)
      if (el && el instanceof HTMLInputElement) el.value = value
    }

    const recognizedCardType = getValue("recognizedCardType:")
    if (recognizedCardType) {
      const { handleCardTypeChange } = this.props
      handleCardTypeChange(recognizedCardType)
    }
  }

  setIFrameLoaded = () => {
    if (this.loadedTimeout !== null) clearTimeout(this.loadedTimeout)
    cuckoo.track(cuckooEvents.PCI_IFRAME_LOADED)
  }

  iframeLoadError = (err: any) => {
    if (this.loadedTimeout !== null) clearTimeout(this.loadedTimeout)
    cuckoo.track(cuckooEvents.PCI_IFRAME_LOAD_ERROR, { err })
  }

  iframeImageLoaded = () => {
    cuckoo.track(cuckooEvents.PCI_IFRAME_IMAGE_LOADED)
  }

  failedToLoadiframeImage = () => {
    // err cannot be converted to JSON, so do not send it to cuckoo
    cuckoo.track(cuckooEvents.PCI_IFRAME_IMAGE_NOT_LOADED)
  }

  render() {
    const { phraseAppId, shouldRequirePaymentSpecialInfo } = this.props
    const domain = getIframeOrigin()

    return (
      <>
        <fieldset hidden>
          <legend>Debug information</legend>
          <label htmlFor="paymentIframe_recognizedCardType">
            Recognized card type
            <input id="paymentIframe_recognizedCardType" type="text" readOnly />
          </label>
          <label htmlFor="paymentIframe_checkValidity">
            Payment informations are valid
            <input id="paymentIframe_checkValidity" type="text" defaultValue="false" readOnly />
          </label>
          <img
            src={`${domain}/images/paymentIcons/visa.png`}
            alt="domain tester"
            onLoad={this.iframeImageLoaded}
            onError={this.failedToLoadiframeImage}
          />
        </fieldset>
        <IframeWrapper>
          <iframe
            id="paymentIframe"
            frameBorder="0"
            title="Payment form"
            permissions="payment"
            allowpaymentrequest="true"
            src={`${domain}/out/${phraseAppId}.html?v5${
              shouldRequirePaymentSpecialInfo ? "#extended" : ""
            }`}
            height={shouldRequirePaymentSpecialInfo ? 164 : 246}
            width="100%"
            onLoad={this.setIFrameLoaded}
            onError={this.iframeLoadError}
          >
            {`iframe on ${domain} domain is used for payment`}
            Please allow this iframe/domain in your adblock/privacy manager
            <script>missingIframe();</script>
          </iframe>
          <Alert
            type="warning"
            title="If you don't see a payment form, please disable all adblockers and reload the page."
          />
        </IframeWrapper>
      </>
    )
  }
}
